import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getRecipe, createRecipe, updateRecipe,
  Recipe, RecipeIngredient, RecipeStep,
  RECIPE_TYPES, RECIPE_STATUSES, STATIONS,
} from './api';
import './recipes.css';

const ADMIN_ROLES = ['Owner', 'Admin'];

// Minimal inventory item shape for autocomplete
interface InvItem { id: number; name: string; base_unit: string; }

// Blank row factories
const blankIngredient = (): Partial<RecipeIngredient> => ({
  ingredient_type: 'Raw Material',
  ingredient_id: undefined,
  sub_recipe_id: undefined,
  quantity: undefined,
  unit: '',
  quantity_text: '',
  is_unscalable: false,
  needs_review: false,
  preparation_note: '',
  is_optional: false,
  is_costable: true,
});

const blankStep = (): Partial<RecipeStep> => ({
  step_number: undefined,
  instruction: '',
  station: undefined,
  staff_note: '',
  critical_control_point: false,
});

const RecipeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { sessionToken, user } = useAuth();
  const navigate = useNavigate();

  // Header fields
  const [name, setName] = useState('');
  const [recipeType, setRecipeType] = useState<string>(RECIPE_TYPES[0]);
  const [category, setCategory] = useState('');
  const [yieldQty, setYieldQty] = useState('');
  const [yieldUnit, setYieldUnit] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [shelfLife, setShelfLife] = useState('');
  const [storageInstructions, setStorageInstructions] = useState('');
  const [status, setStatus] = useState('Draft');
  const [notes, setNotes] = useState('');

  const [ingredients, setIngredients] = useState<Partial<RecipeIngredient>[]>([blankIngredient()]);
  const [steps, setSteps] = useState<Partial<RecipeStep>[]>([blankStep()]);

  // Inventory items for ingredient lookup
  const [invItems, setInvItems] = useState<InvItem[]>([]);
  // Sub-recipe options
  const [subRecipes, setSubRecipes] = useState<Recipe[]>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingRecipe, setLoadingRecipe] = useState(isEdit);

  const isAdmin = ADMIN_ROLES.includes(user?.role || '');

  // Load inventory items for dropdown
  useEffect(() => {
    fetch('/api/v2/inventory', {
      headers: { 'X-Session-Token': sessionToken || '' },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.items) setInvItems(d.items); })
      .catch(() => {});
  }, [sessionToken]);

  // Load sub-recipes for dropdown
  useEffect(() => {
    fetch('/api/sop/recipes?status=Active', {
      headers: { 'X-Session-Token': sessionToken || '' },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.recipes) setSubRecipes(d.recipes); })
      .catch(() => {});
  }, [sessionToken]);

  // Load recipe for edit
  const loadRecipe = useCallback(() => {
    if (!isEdit || !id) return;
    getRecipe(sessionToken, parseInt(id, 10))
      .then(({ recipe: r, ingredients: ings, steps: sps }) => {
        setName(r.name);
        setRecipeType(r.recipe_type);
        setCategory(r.category || '');
        setYieldQty(r.yield_quantity?.toString() || '');
        setYieldUnit(r.yield_unit || '');
        setPrepTime(r.prep_time_minutes?.toString() || '');
        setCookTime(r.cook_time_minutes?.toString() || '');
        setShelfLife(r.shelf_life || '');
        setStorageInstructions(r.storage_instructions || '');
        setStatus(r.status);
        setNotes(r.notes || '');
        setIngredients(ings.length ? ings : [blankIngredient()]);
        setSteps(sps.length ? sps : [blankStep()]);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoadingRecipe(false));
  }, [isEdit, id, sessionToken]);

  useEffect(() => { loadRecipe(); }, [loadRecipe]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) { setError('Recipe name is required'); return; }
    if (!recipeType) { setError('Recipe type is required'); return; }

    // Validate steps
    const validSteps = steps.filter(s => s.instruction?.trim());
    if (validSteps.length === 0) { setError('At least one step with an instruction is required'); return; }

    // Validate ingredients
    const validIngredients = ingredients.filter(ing => {
      if (ing.ingredient_type === 'Raw Material') return ing.ingredient_id;
      if (ing.ingredient_type === 'Sub Recipe') return ing.sub_recipe_id;
      return false;
    });

    const payload = {
      name: name.trim(),
      recipe_type: recipeType as import('./api').RecipeType,
      category: category || null,
      yield_quantity: yieldQty ? parseFloat(yieldQty) : null,
      yield_unit: yieldUnit || null,
      prep_time_minutes: prepTime ? parseInt(prepTime, 10) : null,
      cook_time_minutes: cookTime ? parseInt(cookTime, 10) : null,
      shelf_life: shelfLife || null,
      storage_instructions: storageInstructions || null,
      status: status as import('./api').RecipeStatus,
      notes: notes || null,
      ingredients: validIngredients.map((ing, idx) => ({
        ingredient_type: ing.ingredient_type,
        ingredient_id: ing.ingredient_type === 'Raw Material' ? ing.ingredient_id : null,
        sub_recipe_id: ing.ingredient_type === 'Sub Recipe' ? ing.sub_recipe_id : null,
        quantity: ing.quantity != null && String(ing.quantity) !== '' ? Number(ing.quantity) : null,
        unit: ing.unit || null,
        quantity_text: ing.quantity_text || null,
        is_unscalable: ing.is_unscalable || false,
        needs_review: ing.needs_review || false,
        preparation_note: ing.preparation_note || null,
        is_optional: ing.is_optional || false,
        is_costable: ing.is_costable !== false,
        sort_order: idx,
      })),
      steps: validSteps.map((step, idx) => ({
        step_number: idx + 1,
        instruction: step.instruction!.trim(),
        station: step.station || null,
        staff_note: step.staff_note || null,
        critical_control_point: step.critical_control_point || false,
        sort_order: idx,
      })),
    };

    setSaving(true);
    try {
      if (isEdit && id) {
        await updateRecipe(sessionToken, parseInt(id, 10), payload);
        navigate(`/recipes/${id}`);
      } else {
        const res = await createRecipe(sessionToken, payload);
        navigate(`/recipes/${res.recipe.id}`);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Ingredient helpers
  const updateIngredient = (idx: number, patch: Partial<RecipeIngredient>) => {
    setIngredients(prev => prev.map((ing, i) => i === idx ? { ...ing, ...patch } : ing));
  };
  const addIngredient = () => setIngredients(prev => [...prev, blankIngredient()]);
  const removeIngredient = (idx: number) => setIngredients(prev => prev.filter((_, i) => i !== idx));

  // Step helpers
  const updateStep = (idx: number, patch: Partial<RecipeStep>) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };
  const addStep = () => setSteps(prev => [...prev, blankStep()]);
  const removeStep = (idx: number) => setSteps(prev => prev.filter((_, i) => i !== idx));

  if (loadingRecipe) return <div className="rcp-loading">Loading recipe...</div>;

  const canSetActive = isAdmin;

  return (
    <div className="rcp-page">
      <Link to={isEdit ? `/recipes/${id}` : '/recipes'} className="rcp-back-link">
        ← {isEdit ? 'Back to Recipe' : 'Back to Recipes'}
      </Link>
      <div className="rcp-header">
        <h1>{isEdit ? '✏️ Edit Recipe' : '➕ New Recipe'}</h1>
        {isEdit && (
          <Link to={`/recipes/${id}/sop`} className="rcp-btn" target="_blank" rel="noopener noreferrer">
            📋 SOP View
          </Link>
        )}
      </div>

      {error && <div className="rcp-error">{error}</div>}

      <form onSubmit={handleSave} className="rcp-form">
        {/* ── Header section ── */}
        <div className="rcp-form-section">
          <h3>Recipe Details</h3>
          <div className="rcp-form-grid">
            <div className="rcp-form-group rcp-span-full">
              <label>Recipe Name *</label>
              <input
                className="rcp-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Garlic Mayo"
                required
              />
            </div>

            <div className="rcp-form-group">
              <label>Type *</label>
              <select className="rcp-select" value={recipeType} onChange={e => setRecipeType(e.target.value)} required>
                {RECIPE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="rcp-form-group">
              <label>Category</label>
              <input className="rcp-input" value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Sauces" />
            </div>

            <div className="rcp-form-group">
              <label>Status</label>
              <select
                className="rcp-select"
                value={status}
                onChange={e => setStatus(e.target.value)}
                disabled={!canSetActive && status !== 'Draft'}
              >
                {RECIPE_STATUSES.filter(s => canSetActive || s === 'Draft').map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="rcp-form-group">
              <label>Yield Quantity</label>
              <input className="rcp-input" type="number" step="0.01" value={yieldQty} onChange={e => setYieldQty(e.target.value)} placeholder="e.g. 0.5" />
            </div>

            <div className="rcp-form-group">
              <label>Yield Unit</label>
              <input className="rcp-input" value={yieldUnit} onChange={e => setYieldUnit(e.target.value)} placeholder="e.g. cup, L, portions" />
            </div>

            <div className="rcp-form-group">
              <label>Prep Time (min)</label>
              <input className="rcp-input" type="number" value={prepTime} onChange={e => setPrepTime(e.target.value)} placeholder="e.g. 5" />
            </div>

            <div className="rcp-form-group">
              <label>Cook Time (min)</label>
              <input className="rcp-input" type="number" value={cookTime} onChange={e => setCookTime(e.target.value)} placeholder="e.g. 10" />
            </div>

            <div className="rcp-form-group">
              <label>Shelf Life</label>
              <input className="rcp-input" value={shelfLife} onChange={e => setShelfLife(e.target.value)} placeholder="e.g. 3 days refrigerated" />
            </div>

            <div className="rcp-form-group rcp-span-full">
              <label>Storage Instructions</label>
              <textarea className="rcp-textarea" value={storageInstructions} onChange={e => setStorageInstructions(e.target.value)} rows={2} placeholder="e.g. Store in airtight container, refrigerate at 4°C" />
            </div>

            <div className="rcp-form-group rcp-span-full">
              <label>Notes</label>
              <textarea className="rcp-textarea" value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Internal notes for kitchen staff" />
            </div>
          </div>
        </div>

        {/* ── Ingredients section ── */}
        <div className="rcp-form-section">
          <h3>Ingredients</h3>
          <div className="rcp-row-list">
            {ingredients.map((ing, idx) => (
              <div key={idx} className="rcp-ingredient-row" style={{ gridTemplateColumns: '140px 2fr 80px 100px 100px auto' }}>
                {/* Type selector */}
                <select
                  className="rcp-select"
                  value={ing.ingredient_type || 'Raw Material'}
                  onChange={e => updateIngredient(idx, { ingredient_type: e.target.value as any, ingredient_id: undefined, sub_recipe_id: undefined })}
                >
                  <option value="Raw Material">Ingredient</option>
                  <option value="Sub Recipe">Sub-Recipe</option>
                </select>

                {/* Ingredient or sub-recipe picker */}
                {ing.ingredient_type === 'Sub Recipe' ? (
                  <select
                    className="rcp-select"
                    value={ing.sub_recipe_id || ''}
                    onChange={e => updateIngredient(idx, { sub_recipe_id: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                  >
                    <option value="">— Select Sub-Recipe —</option>
                    {subRecipes.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    className="rcp-select"
                    value={ing.ingredient_id || ''}
                    onChange={e => {
                      const item = invItems.find(i => i.id === parseInt(e.target.value, 10));
                      updateIngredient(idx, {
                        ingredient_id: e.target.value ? parseInt(e.target.value, 10) : undefined,
                        unit: item?.base_unit || ing.unit,
                      });
                    }}
                  >
                    <option value="">— Select Ingredient —</option>
                    {invItems.map(item => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                )}

                {/* Quantity */}
                <input
                  className="rcp-input"
                  type="number"
                  step="0.001"
                  placeholder="Qty"
                  value={ing.quantity ?? ''}
                  onChange={e => updateIngredient(idx, { quantity: e.target.value ? parseFloat(e.target.value) : undefined })}
                />

                {/* Unit */}
                <input
                  className="rcp-input"
                  placeholder="Unit"
                  value={ing.unit || ''}
                  onChange={e => updateIngredient(idx, { unit: e.target.value })}
                />

                {/* Prep note */}
                <input
                  className="rcp-input"
                  placeholder="Prep note"
                  value={ing.preparation_note || ''}
                  onChange={e => updateIngredient(idx, { preparation_note: e.target.value })}
                />

                {/* Remove */}
                <button type="button" className="rcp-remove-btn" onClick={() => removeIngredient(idx)} title="Remove">✕</button>
              </div>
            ))}
          </div>
          <button type="button" className="rcp-add-row-btn" onClick={addIngredient}>
            + Add Ingredient
          </button>
        </div>

        {/* ── Steps section ── */}
        <div className="rcp-form-section">
          <h3>Steps</h3>
          <div className="rcp-row-list">
            {steps.map((step, idx) => (
              <div key={idx} className="rcp-step-row" style={{ gridTemplateColumns: '30px 1fr 140px 100px auto' }}>
                <span className="rcp-text-muted" style={{ fontWeight: 700, textAlign: 'center', paddingTop: '0.4rem' }}>
                  {idx + 1}
                </span>
                <input
                  className="rcp-input"
                  placeholder={`Step ${idx + 1} instruction...`}
                  value={step.instruction || ''}
                  onChange={e => updateStep(idx, { instruction: e.target.value })}
                />
                <select
                  className="rcp-select"
                  value={step.station || ''}
                  onChange={e => updateStep(idx, { station: e.target.value as any || undefined })}
                >
                  <option value="">— Station —</option>
                  {STATIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <label className="rcp-checkbox-label" style={{ justifyContent: 'center' }}>
                  <input
                    type="checkbox"
                    checked={step.critical_control_point || false}
                    onChange={e => updateStep(idx, { critical_control_point: e.target.checked })}
                  />
                  CCP
                </label>
                <button type="button" className="rcp-remove-btn" onClick={() => removeStep(idx)} title="Remove">✕</button>
              </div>
            ))}
          </div>
          <button type="button" className="rcp-add-row-btn" onClick={addStep}>
            + Add Step
          </button>
        </div>

        {/* ── Actions ── */}
        <div className="rcp-form-actions">
          <Link to={isEdit ? `/recipes/${id}` : '/recipes'} className="rcp-btn">Cancel</Link>
          <button type="submit" className="rcp-btn rcp-btn-primary" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Recipe'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RecipeForm;
